import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { redactConfiguration } from "./redactConfiguration";
import { ConfigurationSupportSnapshot } from "./types";

interface PublishedSnapshot {
  id: string;
  priority: number;
  sequence: number;
  snapshot: ConfigurationSupportSnapshot;
}

interface ConfigurationSupportContextValue {
  active: ConfigurationSupportSnapshot | null;
  publish: (id: string, snapshot: ConfigurationSupportSnapshot, priority: number) => void;
  clear: (id: string) => void;
}

const ConfigurationSupportContext = createContext<ConfigurationSupportContextValue | null>(null);

export const ConfigurationSupportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<PublishedSnapshot[]>([]);
  const sequence = useRef(0);

  const publish = useCallback((id: string, snapshot: ConfigurationSupportSnapshot, priority: number) => {
    sequence.current += 1;
    const nextSequence = sequence.current;
    const safeSnapshot: ConfigurationSupportSnapshot = {
      ...snapshot,
      configuration: redactConfiguration(snapshot.configuration) as Record<string, unknown>,
    };
    setEntries((current) => [
      ...current.filter((entry) => entry.id !== id),
      { id, priority, sequence: nextSequence, snapshot: safeSnapshot },
    ]);
  }, []);

  const clear = useCallback((id: string) => {
    setEntries((current) => (current.some((entry) => entry.id === id) ? current.filter((entry) => entry.id !== id) : current));
  }, []);

  const active = useMemo(() => {
    const activeEntry = entries.reduce<PublishedSnapshot | null>((best, entry) => {
      if (!best) return entry;
      if (entry.priority > best.priority) return entry;
      if (entry.priority === best.priority && entry.sequence > best.sequence) return entry;
      return best;
    }, null);
    return activeEntry?.snapshot ?? null;
  }, [entries]);

  const value = useMemo<ConfigurationSupportContextValue>(
    () => ({ active, publish, clear }),
    [active, publish, clear]
  );

  return (
    <ConfigurationSupportContext.Provider value={value}>{children}</ConfigurationSupportContext.Provider>
  );
};

export function useConfigurationSupportSnapshot(): ConfigurationSupportSnapshot | null {
  return useContext(ConfigurationSupportContext)?.active ?? null;
}

export function usePublishConfigurationSupport(
  id: string,
  snapshot: ConfigurationSupportSnapshot | null,
  priority: number
): void {
  const context = useContext(ConfigurationSupportContext);
  const publish = context?.publish;
  const clear = context?.clear;
  const serialized = snapshot ? JSON.stringify(snapshot) : "";
  useEffect(() => {
    if (!publish || !clear) return undefined;
    if (!snapshot) {
      clear(id);
      return () => clear(id);
    }
    publish(id, { ...snapshot, pagePath: window.location.pathname }, priority);
    return () => clear(id);
    // The serialized snapshot is the change signal. `snapshot` is read from that same render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publish, clear, id, priority, serialized]);
}
